// penOnlyMode — Palm Rejection (= Apple Pencil / Wacom 液タブ等の `pointerType
// === 'pen'` 以外を canvas で無視する) のための pure helper (consult 2026-05-09).
//
// ## 背景・経緯
//
// iPad で Apple Pencil で描いていると、誤って指タッチで描画してしまう「指誤爆」
// がある. CSP / Procreate / Photoshop でも標準の Palm Rejection を新版に移植.
// 「ペン専用モード」 ON のとき、`pointerType === 'pen'` 以外 (= mouse / touch)
// を canvas で無視する.
//
// 液タブ (= Wacom Cintiq 等) も `pointerType === 'pen'` で報告されるので、
// Apple Pencil と同じロジックで動作.
//
// ## 仕様 (consult 2026-05-09 確定)
//
// - state: `penOnlyMode: boolean` 単一の state、session-local (= localStorage 不使用、
//   ページ再読み込みでリセット).
// - 自動 ON: `pointerType === 'pen'` を検知したら **毎回** `setPenOnlyMode(true)`
//   を呼ぶ. 「ペンで描いたら毎回 ON に戻る」「マウスで一時的に描きたければ
//   チェックボックスを OFF」「次にペンで描いたらまた ON 復帰」のシンプルなモデル.
// - early return: `penOnlyMode === true` + `pointerType !== 'pen'` のとき
//   `onPointerDown` で全経路を抑止 (stroke / spuit / preview-drag いずれも開始しない).
//
// ## なぜ pure helper として外出ししたか
//
// `packages/web` の vitest は **node env, no DOM**
// (`packages/web/vitest.config.ts:9`). React component や PointerEvent を
// node 環境で素直に組み立てられないため、ロジックを pure 関数に切り出して
// 単体テスト可能にする. 既存 `spuit.ts` / `getPenWidthCellDotDisplay` と同じ流儀.
// `DrawingCanvas.tsx` 側はこれらを組み合わせるだけで自前のテストは不要.
//
// ## β 期の拡張可能性
//
// α は session-local で割り切るが、Modern Layout (β) で `UserSettingsModal`
// に永続化拡張可能. その場合は localStorage アダプタを別レイヤで追加し、
// この helper は引き続き pure な判定関数として再利用する.

/**
 * `PointerEvent.pointerType` は仕様上 `string` (= 将来の拡張に備えた open enum:
 * `'mouse' | 'pen' | 'touch' | ''` など). pen 専用モードの判定は文字列比較で
 * 十分なので narrow しない (= "pen" 以外を一律「ペン以外」扱い).
 */
export type PointerTypeLike = string;

/**
 * pointerdown 時に「ペン専用モード を自動 ON 復帰させるべきか」の判定.
 *
 * - `pointerType === 'pen'` (= Apple Pencil / Wacom 液タブ等) を検知したら true.
 * - 既に ON でも true を返す (= caller が冪等に setPenOnlyMode(true) を呼ぶ
 *   想定だが、StrictMode dev 二重実行時の余計な setState を避けたい場合は
 *   caller 側で `if (!penOnlyMode)` 条件をかけて呼び出しを skip する).
 *
 * 「ペンで描いたら毎回 ON に戻る」のシンプルなモデル (consult 2026-05-09).
 * ユーザがマウスで一時 OFF にしてもペンを再度使ったら自動復帰する.
 */
export const shouldAutoEnablePenOnlyMode = (pointerType: PointerTypeLike): boolean => {
  return pointerType === 'pen';
};

/**
 * pointerdown 時に「ペン専用モードのため pointer event を無視すべきか」の判定.
 *
 * - `penOnlyMode === true` + `pointerType !== 'pen'` のとき true (= 無視).
 * - `penOnlyMode === false` のときは常に false (= 既存挙動、全 pointer 受け付け).
 * - `pointerType === 'pen'` のときは常に false (= ペンは ON でも無視しない).
 *
 * caller (= `DrawingCanvas.onPointerDown`) は true の場合 stroke / spuit /
 * preview-drag いずれも開始せず early return する (= `setPointerCapture` も
 * 呼ばない、`activeRef` も触らない). pointermove / pointerup は activeRef が
 * null のままなので既存 early return で自然に skip される.
 */
export const shouldSkipPointerForPenOnlyMode = (
  pointerType: PointerTypeLike,
  penOnlyMode: boolean,
): boolean => {
  if (!penOnlyMode) return false;
  return pointerType !== 'pen';
};

// ---------------------------------------------------------------------------
// 指タッチ無視の通知 (feature/pen-only-finger-notice)
//
// iPad で Pencil 後にアプリを裏へ回し、戻って指で描こうとすると penOnlyMode が
// 残っていて「何も起きない」ため壊れたように見える (作者本番検証)。単独の指タッチが
// ジェスチャに昇格せず終わったとき、短い通知を出して理由を伝える。
// ---------------------------------------------------------------------------

/** 通知を出す最小間隔 (ms)。連打で出ずっぱりにならないための throttle。 */
export const PEN_ONLY_FINGER_NOTICE_THROTTLE_MS = 4500;
/** pen-up 直後、手のひら置きとみなして通知を出さない猶予 (ms)。 */
export const PEN_ONLY_PALM_WINDOW_MS = 1000;
/** 通知の表示時間 (ms)。 */
export const PEN_ONLY_FINGER_NOTICE_DISPLAY_MS = 2500;
/** 通知文言。解除先は Classic=ツールパネル / Modern(phone 含む)=ツールプロパティの「ペン専用」。 */
export const PEN_ONLY_FINGER_NOTICE_TEXT =
  'ペン専用モード中です（指では描けません）\n「ペン専用」のチェックを外すと指でも描けます';

export interface PenOnlyFingerNoticeInput {
  /** 現在時刻 (ms)。 */
  readonly now: number;
  /** 通知を最後に出した時刻。未表示なら null。 */
  readonly lastShownAt: number | null;
  /** pen が今接地中か (palm rejection 中)。 */
  readonly penActive: boolean;
  /** 最後に pen が離れた時刻。未使用なら null。 */
  readonly lastPenUpAt: number | null;
}

/**
 * 単独の指タッチが (ジェスチャに昇格せず) 終わったときに通知を出すか (pure)。
 * pen 接地中 / pen-up 直後 (手のひら置き) / throttle 内は false。
 */
export const shouldShowPenOnlyFingerNotice = (input: PenOnlyFingerNoticeInput): boolean => {
  if (input.penActive) return false;
  if (input.lastPenUpAt !== null && input.now - input.lastPenUpAt < PEN_ONLY_PALM_WINDOW_MS) {
    return false;
  }
  if (
    input.lastShownAt !== null &&
    input.now - input.lastShownAt < PEN_ONLY_FINGER_NOTICE_THROTTLE_MS
  ) {
    return false;
  }
  return true;
};
